import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import {
  getAllMedicationByWorkspaceId,
  getPresMediList,
  setPrescriptionArchieve,
} from "../../Api/Services/MedicationServices.js";
import { getPetNamesByWorkspaceId } from "../../Api/Services/PetServices.js";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider/AuthProvider.js";
import { filterAscDescData, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { useTheme } from "@mui/material/styles";

import "./GlobalItemList.css";
import { Button } from "@mui/material";

export default function PrescriptionAndMedication() {
  const { workspaceId: w_id } = useAuth();
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveMedicationId, setArchiveMedicationId] = useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [prescriptionData, setPrescriptionData] = useState([]);
  const fetchIdRef = useRef(0);

  const navigate = useNavigate();
  const [selectedPetName, setSelectedPetName] = useState("");

  const handleArchiveClick = (medication_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveMedicationId(medication_id); 
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setPrescriptionArchieve(archiveMedicationId)
      .then((response) => {
        setIsUpdating(false);
        console.log(response);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };
  const theme = useTheme();

  const { data = [], isLoading } = useSWR(
    w_id ? `AllMedication${w_id}` : null,
    async () => {
      const { data } = await getAllMedicationByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort, ...props } = filter;
    if (filter && filter.from > -1) {
      if (data?.length && limit) {
        setPageCount(Math.ceil((data?.length) / limit));
      }
      const allData = [...data];
      const filteredResult = filterAscDescData(allData, sort, from, limit);
      setPrescriptionData(filteredResult);
    }
  }, [filter, data]);

  const { data: medicationData = [] } = useSWR(
    `medicationData`,
    async () => {
      const { data } = await getPresMediList();
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const result = prescriptionData
    ? prescriptionData.map((medication) => {
        const {
          pet_id,
          dosage,
          image,
          name,
          type,
          vet_practice_name,
          medication_id,
        } = medication;
        const matchingMedication = medicationData.find(
          (medication) => medication.id === medication_id
        );

      return {
          id: medication_id,
          pet_id,
          medication_id,
          image,
          name,
          type,
          vet_practice_name,
          dosage: matchingMedication ? matchingMedication.dosage : dosage,
        };
      })
    : [];

  let filteredData;

  if (selectedPetName === "") {
    filteredData = result;
  } else if (!result) {
    filteredData = [];
  } else {
    filteredData = prescriptionData.filter((item) => {
      const petName = item.name.toLowerCase() || "";
      const query = selectedPetName.toLowerCase();
      return petName.includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const defaultOptions = {
    pageSize: 5,
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Medication Type`,
        accessor: "type",
        width: "12.5vw",
      },
      {
        Header: "Prescribing Vet",
        accessor: "vet_practice_name",
        width: "12.5vw",
      },
      {
        Header: "Dosage",
        accessor: "dosage",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "medication_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.medication_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background:`${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
             <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  }, []);

  const props = {
    label: "Prescriptions and Medications",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading: isLoading,
    dataAddLink: "/prescription-and-medication/Form",
    columns,
    columnsData: filteredData,
    viewDataLink: (data) =>
      `/prescription-and-medication/prescription-info/${data.medication_id}`,
    placeholder: "pet name",
    details: " There are no prescription and medication details for this user.",
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "Yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "No",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this Prescription and Medication?"
            handleClose={()=> setOpen(false)}
          />
        </>
      )}
    </>
  );
}
